import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from "react";
import styled, { css } from "styled-components";
import _ from "lodash";

import { IProductStatus } from "src/core/products/api/products.api";
import ProductStatusRow from "src/core/products/components/ProductStatuses/ProductStatusRow";
import NewFutureStatus from "src/core/products/components/ProductStatuses/NewFutureStatus";
import { Text } from "src/library/components/Text";
import { Button } from "src/library/components/Button";

interface FutureProductStatusesProps {
  productStatuses: ProductStatusRowType[],
  productId: number;
  displayOnly?: boolean;
}

export type ProductStatusRowType = {
  localId?: string;
} & IProductStatus;

/**
 * Returns list of future statuses. Each row with status, start and end date.
 * Includes functionality to add, update and remove statuses. Forward ref passed for access to state.
 */
const FutureProductStatuses: React.ForwardRefRenderFunction<{currentFutureStatuses: ProductStatusRowType[]}, FutureProductStatusesProps> = (
  {
    productStatuses,
    productId,
    displayOnly = false
  }, ref) => {
  const [ currentFutureStatuses, setCurrentFutureStatuses ] = useState<ProductStatusRowType[] | []>(productStatuses || []);
  const [ newFutureStatusFormOpen, setNewFutureStatusFormOpen ] = useState<boolean>(false);

  const handleUpdateFutureStatuses = useCallback((updatedProductStatus: ProductStatusRowType) => {
    setCurrentFutureStatuses(currentStatuses => _.map(currentStatuses, status => (
      (status.id || status.localId) === updatedProductStatus.id ? updatedProductStatus : status
    )));
  }, []);

  const handleAddNewFutureStatus = useCallback((newFutureStatus: ProductStatusRowType) => {
    setCurrentFutureStatuses(currentStatuses => [ ...currentStatuses, newFutureStatus ]);
    setNewFutureStatusFormOpen(false);
  }, []);

  const handleRemoveFutureStatus = useCallback((statusToDeleteId: string | number | undefined) => {
    if (!statusToDeleteId) {
      return;
    }

    setCurrentFutureStatuses(currentStatuses => _.filter(
      currentStatuses, status => status.id ? status.id !== statusToDeleteId : status.localId !== statusToDeleteId
    ));
  }, []);

  useImperativeHandle(ref, () => ({ currentFutureStatuses }), [ currentFutureStatuses ]);

  // if new array of product statuses provided, reset currentFutureStatues with update.
  useEffect(() => {
    setCurrentFutureStatuses(productStatuses);
  }, [ productStatuses ]);

  if (productStatuses.length === 0 && displayOnly) {
    return null;
  }

  return (
    <ComponentWrapper>
      <Text
        as="h3"
        fontSize="h3"
      >
        Future Statuses
      </Text>

      <Text
        as="p"
        weight="light"
      >
        Suspending a product will remove the product from all orders where the delivery date falls within the date range.
      </Text>

      <Text
        as="p"
        weight="light"
      >
        Delisting will remove the product from all orders and lists where the delivery date is after the start date.
      </Text>

      {currentFutureStatuses.length > 0 && currentFutureStatuses.map((productStatus: ProductStatusRowType) => (
        <ProductStatusRow
          key={productStatus.id || productStatus.localId}
          productId={productId}
          localId={productStatus.localId}
          productStatus={productStatus}
          updateFutureStatuses={itemToUpdate => handleUpdateFutureStatuses(itemToUpdate)}
          onDelete={itemToDelete => handleRemoveFutureStatus(itemToDelete)}
          disabled
          deleteEnabled={!displayOnly}
        />
      ))}

      {!newFutureStatusFormOpen && !displayOnly && (
        <Button onClick={() => setNewFutureStatusFormOpen(true)}>
          Add Future Status
        </Button>
      )}

      {newFutureStatusFormOpen && (
        <NewFutureStatus
          productId={productId}
          onSave={itemToAdd => handleAddNewFutureStatus(itemToAdd)}
          onCancel={() => setNewFutureStatusFormOpen(false)}
        />
      )}
    </ComponentWrapper>
  );
};

const ComponentWrapper = styled.div`
  ${({ theme }) => css`
    padding: 0 0 ${theme.spacing.padding.lg} 0;
  `}
`;

export default React.forwardRef(FutureProductStatuses);
